/**
 * Puestos y zonas: los dos catálogos editables que usa la bolsa de empleo.
 *
 * Viven en Configuración (`settings/puestos` y `settings/zones`) para que el
 * equipo pueda añadir «socorrista» sin esperar a un despliegue. Se piden una
 * vez por sesión y se guardan en memoria: los mira la lista, la ficha, el
 * formulario y el panel de la IA, y son dos listas de diez elementos que no
 * cambian de un minuto para otro.
 */
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

export interface OpcionCatalogo {
  id: string;
  label: string;
}

export interface Catalogos {
  puestos: OpcionCatalogo[];
  zonas: OpcionCatalogo[];
  /** Mapas id → etiqueta, que es como se consultan al pintar. */
  puestoLabel: Record<string, string>;
  zonaLabel: Record<string, string>;
}

const VACIO: Catalogos = { puestos: [], zonas: [], puestoLabel: {}, zonaLabel: {} };

let cache: Catalogos | null = null;
let enVuelo: Promise<Catalogos> | null = null;

function comoMapa(opciones: OpcionCatalogo[]): Record<string, string> {
  return Object.fromEntries(opciones.map((o) => [o.id, o.label]));
}

async function cargar(): Promise<Catalogos> {
  const ajustes = await api.getSettings();
  const valores = (clave: string): OpcionCatalogo[] =>
    (ajustes.find((a: { key?: string; id?: string }) => a.key === clave || a.id === clave)?.values ||
      []) as OpcionCatalogo[];

  const puestos = valores('puestos');
  const zonas = valores('zones');
  return { puestos, zonas, puestoLabel: comoMapa(puestos), zonaLabel: comoMapa(zonas) };
}

/** Fuerza una recarga: lo llama Configuración al guardar los puestos. */
export function olvidarCatalogos(): void {
  cache = null;
  enVuelo = null;
}

export function useCatalogos(): { catalogos: Catalogos; cargando: boolean } {
  const [catalogos, setCatalogos] = useState<Catalogos>(cache || VACIO);
  const [cargando, setCargando] = useState(!cache);

  useEffect(() => {
    if (cache) return;
    let vigente = true;
    // Una sola petición aunque monten tres componentes a la vez.
    enVuelo = enVuelo || cargar();
    enVuelo
      .then((c) => {
        cache = c;
        if (vigente) setCatalogos(c);
      })
      .catch(() => {
        // Sin catálogo se pintan los ids crudos, que es feo pero funciona.
        enVuelo = null;
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });
    return () => {
      vigente = false;
    };
  }, []);

  return { catalogos, cargando };
}
